// src/pages/VideoGallery.tsx
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Loader2, Play, Video as VideoIcon, Calendar } from "lucide-react";

interface MediaItem {
  id: number;
  title: string;
  type: "video";
  storage_type: "file" | "link";
  url: string;
  thumbnail_url: string | null;
  created_at: string;
}

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/video-gallery`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;
const backendUrl = "https://www.sevaafoundation.com/seva-backend";


const VideoGallery = () => {
  const [videos, setVideos] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await fetch(
        `${backendUrl}/api/media/public_list.php?type=video`
      );

      const data = await response.json();

      if (data.success) {
        setVideos(data.media);
      }
    } catch (err) {
      console.error("Failed to fetch videos:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const getVideoSrc = (video: MediaItem) => {
    if (!video.url) {
      return "";
    }

    if (video.storage_type === "link" || video.url.startsWith("http://") || video.url.startsWith("https://")) {
      return video.url;
    }

    return `${backendUrl}${video.url}`;
  };

  const getThumbnailSrc = (video: MediaItem) => {
    if (!video.thumbnail_url) {
      return defaultImage;
    }

    if (video.thumbnail_url.startsWith("http://") || video.thumbnail_url.startsWith("https://")) {
      return video.thumbnail_url;
    }

    return `${backendUrl}${video.thumbnail_url}`;
  };

  const formatDate = (date?: string) => {
    if (!date) return "SEVAA Foundation";

    return new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const galleryOgImage = videos.length > 0 ? getThumbnailSrc(videos[0]) : defaultImage;

  const videoGallerySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${pageUrl}#collectionpage`,
    name: "Video Gallery | SEVAA Foundation",
    alternateName: "SEVAA Foundation NGO Video Gallery",
    url: pageUrl,
    inLanguage: ["en-IN", "hi-IN"],
    description:
      "Watch SEVAA Foundation videos featuring NGO field visits, social welfare programs, child education, healthcare camps, food donation, women empowerment, tree plantation, elderly care, volunteer work and CSR impact stories.",
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      name: "SEVAA Foundation",
      url: siteUrl,
    },
    publisher: {
      "@type": "NGO",
      "@id": `${siteUrl}/#ngo`,
      name: "SEVAA Foundation",
      alternateName: "Swadesh Sevaa Charitable Foundation",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: logoImage,
      },
      areaServed: [
        {
          "@type": "Country",
          name: "India",
        },
        {
          "@type": "Place",
          name: "Noida",
        },
        {
          "@type": "Place",
          name: "Jharkhand",
        },
        {
          "@type": "Place",
          name: "Chatra",
        },
        {
          "@type": "Place",
          name: "Pakur",
        },
      ],
    },
    about: [
      "NGO Video Gallery",
      "Social Welfare Videos",
      "Child Education",
      "Healthcare Support",
      "Food Donation",
      "Women Empowerment",
      "Tree Plantation",
      "Old Age Support",
      "Volunteer Work",
      "CSR Impact",
      "Community Outreach",
      "Rural Development",
      "Tribal Community Support",
    ],
    hasPart: videos.map((video) => ({
      "@type": "VideoObject",
      name: video.title,
      description: `${video.title} - SEVAA Foundation NGO video and social impact story.`,
      thumbnailUrl: getThumbnailSrc(video),
      uploadDate: video.created_at,
      contentUrl: getVideoSrc(video),
      embedUrl: video.storage_type === "link" ? video.url : undefined,
      publisher: {
        "@type": "NGO",
        name: "SEVAA Foundation",
        url: siteUrl,
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumb`,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Video Gallery",
        item: pageUrl,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Video Gallery | SEVAA Foundation</title>

        <meta name="title" content="Video Gallery | SEVAA Foundation" />

        <meta name="description" content="Watch SEVAA Foundation video gallery with NGO field visits, education, healthcare camps, food donation and community impact stories." />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />

        <meta
          property="og:title"
          content="Video Gallery | SEVAA Foundation NGO"
        />

        <meta
          property="og:description"
          content="Watch SEVAA Foundation NGO videos from field visits, social welfare programs, education, healthcare, food donation and community outreach."
        />

        <meta property="og:image" content={galleryOgImage} />
        <meta
          property="og:image:alt"
          content="SEVAA Foundation Video Gallery"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="SEVAA Foundation Video Gallery"
        />

        <meta
          name="twitter:description"
          content="Watch NGO field work and social impact videos by SEVAA Foundation."
        />

        <meta name="twitter:image" content={galleryOgImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation video gallery" />

        <script type="application/ld+json">
          {JSON.stringify(videoGallerySchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-slate-50 min-h-screen py-12 px-6 font-poppins">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-12">
            <span className="inline-block text-orange-600 font-black text-xs uppercase tracking-[0.25em] mb-4">
              SEVAA Foundation Impact Videos
            </span>

            <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-4">
              Video Gallery
            </h1>

            <p className="text-slate-600 max-w-3xl mx-auto leading-relaxed text-base md:text-lg">
              Watch SEVAA Foundation&apos;s impactful stories, field visits,
              community outreach, child education work, healthcare camps, food
              donation drives, women empowerment activities, tree plantation
              campaigns, elderly care support and CSR impact in action.
            </p>
          </header>

          {isLoading ? (
            <div className="py-20 flex justify-center items-center gap-3 text-slate-500 font-medium">
              <Loader2 className="animate-spin text-orange-500" size={28} />
              Loading videos...
            </div>
          ) : videos.length === 0 ? (
            <section className="text-center py-20 text-slate-500 border-2 border-dashed border-slate-300 rounded-2xl bg-white">
              <VideoIcon size={48} className="mx-auto text-slate-300 mb-4" />

              <h2 className="text-xl font-bold text-slate-700 mb-2">
                No videos available yet
              </h2>

              <p className="text-slate-500">
                SEVAA Foundation video gallery will be updated soon with NGO
                field stories and social impact videos.
              </p>
            </section>
          ) : (
            <section aria-labelledby="video-list-heading">
              <h2 id="video-list-heading" className="sr-only">
                SEVAA Foundation Video Gallery List
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {videos.map((video) => {
                  const videoSrc = getVideoSrc(video);
                  const thumbnailSrc = getThumbnailSrc(video);

                  return (
                    <article
                      key={video.id}
                      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    >
                      {/* Video Player or Thumbnail */}
                      <div className="relative aspect-video bg-slate-900">
                        {video.storage_type === "file" ? (
                          <video
                            controls
                            className="w-full h-full object-cover"
                            src={videoSrc}
                            poster={thumbnailSrc}
                            preload="metadata"
                            aria-label={`${video.title} - SEVAA Foundation NGO video`}
                          />
                        ) : (
                          <a
                            href={video.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full h-full relative group cursor-pointer"
                            aria-label={`Watch video: ${video.title}`}
                          >
                            {video.thumbnail_url ? (
                              <img
                                src={thumbnailSrc}
                                className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                alt={`${video.title} - SEVAA Foundation video thumbnail`}
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-slate-800">
                                <VideoIcon className="w-12 h-12 text-slate-500" />
                              </div>
                            )}

                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-14 h-14 bg-orange-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                <Play
                                  className="w-6 h-6 text-white ml-1"
                                  fill="currentColor"
                                />
                              </div>
                            </div>
                          </a>
                        )}
                      </div>

                      {/* Details */}
                      <div className="p-5 flex-1 flex flex-col justify-between">
                        <h3 className="font-bold text-slate-900 text-lg line-clamp-2">
                          {video.title}
                        </h3>

                        <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-500 font-medium">
                          <time
                            dateTime={video.created_at}
                            className="flex items-center gap-1.5"
                          >
                            <Calendar size={14} />
                            {formatDate(video.created_at)}
                          </time>

                          {video.storage_type === "link" && (
                            <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded font-bold">
                              External Link
                            </span>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
};

export default VideoGallery;